import React, { Suspense, lazy } from "react";
import { Routes, BrowserRouter, Route } from "react-router-dom";
import ScrollToTop from "./ScrollToTop";
import { useSelector } from "react-redux";
import ProtectedRoute from "./protectedRoute";
import FreeAuthRoute from "./FreeAuthRoute";
import CheckProfile from "./middlewares/CheckProfile";
import IncomingCallHost from "./components/socket/IncomingCallHost";
import CallErrorBoundary from "./components/elements/CallErrorBoundary";
import CallTransitionLoader from "./components/elements/CallTransitionLoader";

// Statically imported Layouts — prevents full layout unmounts, blank flashes, and header/sidebar thrashing
import AuthLayout from "./layouts/AuthLayout";
import DefaultLayout from "./layouts/DefaultLayout";
import InnerLayout from "./layouts/InnerLayout";
import BlankLayout from "./layouts/BlankLayout";

// Lazy-loaded Pages and Components
const Signin = lazy(() => import("./pages/Signin"));
const Home = lazy(() => import("./pages/Home"));
const FindPsychologist = lazy(() => import("./pages/FindPsychologist"));
const HowItWorks = lazy(() => import("./pages/HowItWorks"));
const About = lazy(() => import("./pages/About"));
const Blog = lazy(() => import("./pages/Blog"));
const AreaIntervention = lazy(() => import("./pages/AreaIntervention"));
const CommunityPsychology = lazy(() => import("./pages/CommunityPsychology"));
const Contact = lazy(() => import("./pages/Contact"));
const SignUp = lazy(() => import("./pages/SignUp"));
const Feedback = lazy(() => import("./pages/Feedback"));
const Faq = lazy(() => import("./pages/Faq"));
const ControlPanel = lazy(() => import("./pages/profile/ControlPanel"));
const ConsultantDetails = lazy(() => import("./pages/ConsultantDetails"));
const ForgotPassword = lazy(() => import("./components/sections/signup/ForgotPassword"));
const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));
const ConsultantSignup = lazy(() => import("./pages/profile/ConsultantSignup"));
const InterventionDetails = lazy(() => import("./pages/InterventionDetails"));
const EmailChats = lazy(() => import("./pages/EmailChats"));
const Chats = lazy(() => import("./pages/Chats"));
const MyProfile = lazy(() => import("./pages/profile/MyProfile"));
const Appointments = lazy(() => import("./pages/profile/Appointments"));
const MyClients = lazy(() => import("./pages/profile/MyClients"));
const Reviews = lazy(() => import("./pages/profile/Reviews"));
const AppointmentsDetails = lazy(() => import("./pages/profile/AppuntamentiPassatiDetails"));
const UserEmailDetails = lazy(() => import("./components/items/emailchatBox/userEmailDetails"));
const ClientNoteList = lazy(() => import("./components/items/clientNotes/ClientNoteList"));
const WishList = lazy(() => import("./pages/profile/WishList"));
const TextChat = lazy(() => import("./pages/TextChat"));
const ReviewPage = lazy(() => import("./pages/ReviewPage"));
const TermsandConditions = lazy(() => import("./pages/TermsandConditions"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicy"));
const NoInternet = lazy(() => import("./pages/NoInternet"));
const BlogDetails = lazy(() => import("./components/sections/blogs/BlogDetails"));
const AdminInvoice = lazy(() => import("./components/elements/tabContent/profile/appuntamentidetails/adminInvoice"));

const CreateRoutes = () => {
  const user = useSelector((state) => state?.initialProfileSlice?.data);
  const id = user && user?._id;

  return (
    <BrowserRouter>
      {/* Global ring host — must outlive layout/page changes so Call Now works on
          home, profile, appointments, find-psychologist, mobile & desktop. */}
      <IncomingCallHost />
      <ScrollToTop>
        <Suspense fallback={null}>
          <Routes>
            {/* Auth Layout Pages */}
            <Route element={<AuthLayout />}>
              <Route
                path="/signin"
                element={
                  <FreeAuthRoute>
                    <Signin />
                  </FreeAuthRoute>
                }
              />
              <Route
                path="/signup"
                element={
                  <FreeAuthRoute>
                    <SignUp />
                  </FreeAuthRoute>
                }
              />
              <Route
                path="/signup-consultant"
                element={
                  <FreeAuthRoute>
                    <ConsultantSignup />
                  </FreeAuthRoute>
                }
              />
              <Route
                path="/forgot-password"
                element={
                  <FreeAuthRoute>
                    <ForgotPassword />
                  </FreeAuthRoute>
                }
              />
              <Route path="/feedback" element={<Feedback />} />
              <Route path="/consultant-profile/:id" element={<ConsultantDetails />} />
            </Route>

            {/* Public Marketing & Info Pages (DefaultLayout) */}
            <Route element={<DefaultLayout />}>
              <Route path="/" element={<Home />} />
              <Route
                path="/find-psychologist"
                element={
                  <ProtectedRoute>
                    <FindPsychologist />
                  </ProtectedRoute>
                }
              />
              <Route path="/howitworks" element={<HowItWorks />} />
              <Route path="/about" element={<About />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog-details/:id" element={<BlogDetails />} />
              <Route path="/terms-of-services" element={<TermsandConditions />} />
              <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
              <Route path="/area-of-intervention" element={<AreaIntervention />} />
              <Route path="/community-psychology" element={<CommunityPsychology />} />
              <Route path="/contact-us" element={<Contact />} />
              <Route path="/frequently-asked-questions" element={<Faq />} />
              <Route path="/intervention-details/:id" element={<InterventionDetails />} />
            </Route>

            {/* Protected Dashboard & Profile Pages (InnerLayout) */}
            <Route
              element={
                <ProtectedRoute>
                  <InnerLayout />
                </ProtectedRoute>
              }>
              <Route
                path="/control-panel"
                element={
                  <CheckProfile>
                    <ControlPanel />
                  </CheckProfile>
                }
              />
              <Route path="/profile" element={<MyProfile />} />
              <Route path="/view-online" element={<ConsultantDetails userId={id} />} />
              <Route
                path="/wish-list"
                element={
                  <CheckProfile>
                    <WishList />
                  </CheckProfile>
                }
              />
              <Route
                path="/appointments"
                element={
                  <CheckProfile>
                    <Appointments />
                  </CheckProfile>
                }
              />
              <Route
                path="/clients"
                element={
                  <CheckProfile>
                    <MyClients />
                  </CheckProfile>
                }
              />
              <Route path="/client-notes/:id" element={<ClientNoteList />} />
              <Route
                path="/email"
                element={
                  <CheckProfile>
                    <EmailChats />
                  </CheckProfile>
                }
              />
              <Route path="/email-details/:id" element={<UserEmailDetails />} />
              <Route
                path="/reviews"
                element={
                  <CheckProfile>
                    <Reviews />
                  </CheckProfile>
                }
              />
              <Route path="/appointments-details" element={<AppointmentsDetails />} />
              <Route path="/appointments-details/:id" element={<AppointmentsDetails />} />
            </Route>

            {/* Live Consultation Shells (BlankLayout) */}
            <Route
              element={
                <ProtectedRoute>
                  <BlankLayout />
                </ProtectedRoute>
              }>
              <Route
                path="/text-chat/:id"
                element={
                  <CallErrorBoundary>
                    <Suspense
                      fallback={
                        <CallTransitionLoader
                          title="Connecting to Chat Consultation…"
                          subtitle="Loading your secure consultation room and messages…"
                          dark={true}
                        />
                      }>
                      <TextChat />
                    </Suspense>
                  </CallErrorBoundary>
                }
              />
              <Route
                path="/chats/:id"
                element={
                  <CallErrorBoundary>
                    <Suspense
                      fallback={
                        <CallTransitionLoader
                          title="Connecting to Consultation…"
                          subtitle="Setting up your secure audio/video connection. Please hold on…"
                          dark={true}
                        />
                      }>
                      <Chats />
                    </Suspense>
                  </CallErrorBoundary>
                }
              />
            </Route>

            {/* Standalone Pages */}
            <Route
              path="/review-appointment/:id"
              element={
                <ProtectedRoute>
                  <ReviewPage />
                </ProtectedRoute>
              }
            />
            <Route path="/invoice" element={<AdminInvoice />} />
            <Route path="/no-connection" element={<DefaultLayout><NoInternet /></DefaultLayout>} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </ScrollToTop>
    </BrowserRouter>
  );
};

export default CreateRoutes;
